// Hvor persontokenet ligger mellom to besøk — og hva det koster.
//
// ═══ RISIKOVURDERING AV LAGRINGSVALGET (les før du endrer) ═══════════════════
// Dugnad-tjenesten autentiserer med `Authorization: Bearer <persontoken>`. Et
// bærer-token må være LESBART for JavaScript for å kunne settes i headeren. Det
// utelukker den tryggeste formen — HttpOnly-cookie, slik SKADE COP bruker mot
// hubben — for den kan JS per definisjon ikke lese.
//
// Valgene som faktisk sto åpne, og hva de koster:
//   1. Ingen lagring       — tokenet limes inn hver gang. Tryggest. Men et token
//                            som må limes inn daglig havner i en Signal-tråd
//                            eller på en gul lapp, og DET er verre enn nettleseren.
//   2. sessionStorage      — glemmes når fanen lukkes. Overlever ikke omstart.
//   3. localStorage        — overlever alt. Kan leses av enhver skriptkjøring på
//                            samme opphav, altså av en XSS-feil i denne appen.
//
// VALGT: brukeren bestemmer. «Husk meg på denne maskinen» av = sessionStorage
// (2), på = localStorage (3). Standard er PÅ, fordi eieren har bedt om at man
// ikke må lime inn tokenet hver gang — men valget står synlig i innloggingen med
// en setning om hva det betyr, og «Logg ut» sletter fra begge.
//
// Følgen vi aksepterer: en XSS-feil i denne klienten gir angriperen tokenet.
// Motvekten ligger andre steder og må holdes: all feltavledet tekst settes med
// `textContent` (aldri `innerHTML`) — se ui/dom.ts — og tokens kan trekkes
// tilbake per token-id i tjenesten. Innfører noen `innerHTML` på data herfra, er
// denne avveiningen ugyldig og må gjøres på nytt.

/** Det lille av `Storage` vi bruker. Gjør fila testbar uten nettleser. */
export interface Nøkkellager {
  getItem(nøkkel: string): string | null;
  setItem(nøkkel: string, verdi: string): void;
  removeItem(nøkkel: string): void;
}

export const TOKEN_NØKKEL = 'prosjekt.persontoken';

export interface Lagre {
  /** `localStorage` — overlever at nettleseren lukkes. */
  vedvarende: Nøkkellager | null;
  /** `sessionStorage` — glemmes med fanen. */
  økt: Nøkkellager | null;
}

/**
 * Hent lagrene fra nettleseren. Begge kan mangle: i privat modus eller med
 * blokkerte informasjonskapsler kaster `localStorage` ved oppslag. Da skal appen
 * fortsatt virke — bare uten å huske tokenet.
 */
export function nettleserLagre(): Lagre {
  const prøv = (hent: () => Nøkkellager): Nøkkellager | null => {
    try {
      const l = hent();
      // Skriv/les/slett faktisk: Safari i privat modus lar deg hente objektet,
      // men kaster først når du prøver å skrive i det.
      const prøvenøkkel = '__prosjekt_prøve__';
      l.setItem(prøvenøkkel, '1');
      l.removeItem(prøvenøkkel);
      return l;
    } catch {
      return null;
    }
  };
  return {
    vedvarende: prøv(() => globalThis.localStorage),
    økt: prøv(() => globalThis.sessionStorage),
  };
}

export interface LagretToken {
  token: string;
  /** Lå det i localStorage (overlever omstart) eller bare i fanen? */
  husket: boolean;
}

/**
 * Formkontroll av et persontoken: `dgn_<id>.<hemmelig>`.
 *
 * Dette er en HØFLIGHET, ikke en sikkerhetskontroll — den fanger at man har
 * limt inn feil tekst (en URL, et halvt token) og sier det med en gang, i stedet
 * for at brukeren får et 401 uten å skjønne hvorfor. Om tokenet er ekte avgjør
 * bare tjenesten. Grensene speiler `parseToken` i services/dugnad/src/auth.ts.
 */
export function serUtSomToken(rå: string): boolean {
  return /^dgn_[A-Za-z0-9_-]{8,64}\.[A-Za-z0-9_-]{16,128}$/.test(rå.trim());
}

/** Les tokenet. Fanen har forrang: den er nyeste innlogging. */
export function lesToken(lagre: Lagre): LagretToken | null {
  const iØkt = trygtLes(lagre.økt, TOKEN_NØKKEL);
  if (iØkt) return { token: iØkt, husket: false };
  const iVedvarende = trygtLes(lagre.vedvarende, TOKEN_NØKKEL);
  if (iVedvarende) return { token: iVedvarende, husket: true };
  return null;
}

/**
 * Lagre tokenet. Skriver ALLTID til begge-eller-ingen på en slik måte at det
 * ikke kan bli liggende to steder: gammelt token slettes fra det lageret vi ikke
 * skriver til. Ellers ville et «husk meg av»-valg latt forrige økts token
 * overleve i localStorage.
 */
export function lagreToken(lagre: Lagre, token: string, husk: boolean): void {
  const t = token.trim();
  if (!t) return;
  const mål = husk ? lagre.vedvarende : lagre.økt;
  const annet = husk ? lagre.økt : lagre.vedvarende;
  trygtSlett(annet, TOKEN_NØKKEL);
  trygtSkriv(mål, TOKEN_NØKKEL, t);
}

/** Logg ut: bort fra begge lagre. */
export function glemToken(lagre: Lagre): void {
  trygtSlett(lagre.vedvarende, TOKEN_NØKKEL);
  trygtSlett(lagre.økt, TOKEN_NØKKEL);
}

// Nettleserlagring kan kaste når kvoten er full eller lagring er avslått.
// Ingen av delene skal ta ned appen — man må bare logge inn på nytt neste gang.
function trygtLes(l: Nøkkellager | null, n: string): string | null {
  try { return l?.getItem(n)?.trim() || null; } catch { return null; }
}
function trygtSkriv(l: Nøkkellager | null, n: string, v: string): void {
  try { l?.setItem(n, v); } catch { /* uten lagring må man logge inn på nytt */ }
}
function trygtSlett(l: Nøkkellager | null, n: string): void {
  try { l?.removeItem(n); } catch { /* samme */ }
}

/** Enkelt minne-lager — brukes i tester og som reserve når nettleseren nekter. */
export function minnelager(): Nøkkellager {
  const m = new Map<string, string>();
  return {
    getItem: (n) => m.get(n) ?? null,
    setItem: (n, v) => { m.set(n, v); },
    removeItem: (n) => { m.delete(n); },
  };
}
